import Loader from 'components/Loader';
import SmkAsyncSelect from 'components/SmkAsyncSelect';
import useRequest from 'hooks/useRequest';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { API_BASE_URL, GET_GENDER } from 'utils/constants';

const AssignDoctorModal = ({ isOpen, toggle, selectedItem, myDoctors }) => {
  const [appointmentDateTime, setExpiryDate] = useState(moment().add(7, 'days'));
  const [doctor, setDoctor] = useState(null);
  const [error, setError] = useState('');
  const [expiryError, setExpiryError] = useState('');

  const [getDoctorByRefID, { loading: getDoctorByRefIDLoading }] = useRequest({
    method: 'GET',
  });

  const [updateConsultation, { loading: updateConsultationLoading }] = useRequest({
    onSuccess: () => {
      toast.success('Doctor has been updated.');
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [createTPZDoctor, { loading: createTPZDoctorLoading }] = useRequest({
    method: 'POST',
    url: '/doctor',
  });

  const [assignDoctorRequest, { loading: assignDoctorRequestLaoding }] = useRequest({
    method: 'PUT',
    onSuccess: () => {
      toggle();
      toast.success('Doctor has been assigned successfully');
      window.location.reload();
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const handleAssignDoctor = async (id) => {
    if (appointmentDateTime) {
      await updateConsultation({
        method: 'PUT',
        url: `/consultation/admin/${selectedItem?.id}`,
        body: {
          appointmentDate: appointmentDateTime,
        },
      });
    }

    await assignDoctorRequest({
      url: `/consultation/admin/${selectedItem?.id}/assign?doctorId=${id}`,
    });
  };

  const handleSave = async () => {
    if (isEmpty(doctor)) {
      setError('Please select a doctor');
      return;
    }
    if (isEmpty(appointmentDateTime)) {
      setExpiryError('Please select a appointment date');
      return;
    }

    const isDoctorExist = myDoctors?.some((e) => e?.id === doctor?.id);

    if (isDoctorExist) {
      handleAssignDoctor(doctor?.id);
      return;
    }

    const doctorPayload = {
      firstName: doctor?.firstName,
      lastName: doctor?.lastName,
      displayName: doctor?.displayName,
      gender: doctor?.gender || 0,
      email: doctor?.email,
      phoneNumber: doctor?.phoneNumber ? doctor?.phoneNumber : '',
      areaOfExpertise: !isEmpty(doctor?.specialities) ? doctor?.specialities[0]?.name : 'Doctor',
      notes: doctor.userDescription,
      isSystemDefined: doctor?.isSystemDefined,
      refId: doctor?.id,
      locationRefs: doctor?.locations
        ? doctor?.locations?.map((healthcareFacilityItem) => {
            return {
              timings: healthcareFacilityItem?.timings,
              refId: healthcareFacilityItem?.id,
              healthcareFacilityRef: {
                name: healthcareFacilityItem?.healthcareFacility?.name,
                phoneNumber: healthcareFacilityItem?.healthcareFacility?.phoneNumber,
                email: healthcareFacilityItem?.healthcareFacility?.email,
                address: healthcareFacilityItem?.healthcareFacility?.address,
                refId: healthcareFacilityItem?.healthcareFacility?.id,
              },
            };
          })
        : [],
    };

    const doctorResponse = await createTPZDoctor({
      body: doctorPayload,
    });

    if (doctorResponse?.success !== false) {
      handleAssignDoctor(doctorResponse?.id);
    } else {
      // get by doctor refID
      const tpzDoctor = await getDoctorByRefID({
        url: `/doctor/get-by-refId?refId=${doctor?.id}`,
      });

      if (tpzDoctor?.success !== false) {
        handleAssignDoctor(tpzDoctor?.id);
      } else {
        toast.error('Something went wrong');
      }
    }
  };

  const handleDoctorSelect = (res) => {
    if (error) {
      setError('');
    }

    if (!isEmpty(res)) {
      setDoctor({
        ...res,
        label: `${res?.firstName} ${res?.lastName} - ${res?.areaOfExpertise}`,
        value: res?.id,
      });
    } else {
      setDoctor(null);
    }
  };

  useEffect(() => {
    if (!isEmpty(selectedItem?.doctor)) {
      setDoctor({
        ...selectedItem?.doctor,
        label: `${selectedItem?.doctor?.firstName} ${selectedItem?.doctor?.lastName} - ${selectedItem?.doctor?.areaOfExpertise}`,
        value: selectedItem?.doctor?.id,
      });
      setExpiryDate(moment(selectedItem?.appointmentDateTime));
    }
  }, [selectedItem]);

  return (
    <>
      <Loader
        isActive={
          createTPZDoctorLoading || assignDoctorRequestLaoding || getDoctorByRefIDLoading || updateConsultationLoading
        }
      />
      <Modal size="lg" centered={true} isOpen={isOpen} toggle={toggle}>
        <ModalHeader toggle={toggle}>
          {!isEmpty(selectedItem?.doctor) ? 'Update Assign' : 'Add Assign'} Doctor
        </ModalHeader>
        <ModalBody className="p-4">
          <Row>
            <Col md={6}>
              <FormGroup className="mb-4">
                <Label>
                  Doctor <span className="text-danger">*</span>
                </Label>
                <SmkAsyncSelect
                  isDoctor={true}
                  isHealthcare={true}
                  acceptedKey={'firstName'}
                  acceptedValue={'id'}
                  searchKeyName="name"
                  placeholder="Type doctor name.."
                  fetchUrl={`${API_BASE_URL}/healthcare/doctor/admin/search`}
                  filters={{
                    autoComplete: true,
                    page: 1,
                    size: 20,
                    sortBy: 'id',
                    sortOrder: 1,
                  }}
                  value={!isEmpty(doctor) ? doctor : null}
                  onChange={handleDoctorSelect}
                />
                {!isEmpty(error) && <p className="text-danger mb-0">{error}</p>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-4">
                <Label>
                  Appointment Date <span className="text-danger">*</span>
                </Label>
                <Input
                  type="datetime-local"
                  id="appointmentDateTime"
                  placeholder="Expiry Date"
                  min={moment().format('YYYY-MM-DDTHH:mm')}
                  value={appointmentDateTime ? appointmentDateTime.format('YYYY-MM-DDTHH:mm') : ''}
                  onChange={(e) => {
                    if (expiryError) {
                      setExpiryError('');
                    }
                    setExpiryDate(moment(e.target.value));
                  }}
                />
                {!isEmpty(expiryError) && <p className="text-danger mb-0">{expiryError}</p>}
              </FormGroup>
            </Col>
          </Row>

          <h6>My Doctors</h6>
          <div className="border rounded-2 p-3" style={{ maxHeight: '200px', overflowY: 'auto', overflowX: 'hidden' }}>
            <Row>
              {!isEmpty(myDoctors) ? (
                myDoctors?.map((e) => (
                  <Col md={4}>
                    <button
                      onClick={() => {
                        if (e?.id === doctor?.id) {
                          setDoctor(null);
                        } else {
                          setDoctor({
                            ...e,
                            label: `${e?.firstName} ${e?.lastName} - ${e?.areaOfExpertise}`,
                            value: e?.id,
                          });
                        }
                      }}
                      className="btn btn-light text-start w-100 border-0 bg-light p-3 rounded-2 mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1">
                            {e?.firstName} {e?.lastName}
                          </h6>
                          <p className="mb-0">{e?.areaOfExpertise}</p>
                        </div>

                        {e?.id === doctor?.id && <i className={`fas fa-check-circle text-success fs-4`} />}
                      </div>
                    </button>
                  </Col>
                ))
              ) : (
                <Col md={12}>
                  <h6>Search to add doctors</h6>
                </Col>
              )}
            </Row>
          </div>

          {!isEmpty(doctor) && (
            <div className="mt-4">
              <h6 className="bg-light p-3 text-center rounded-2 mb-3">Selected Doctor Details:</h6>
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-1">
                    Name :{' '}
                    <b>
                      {doctor?.firstName} {doctor?.lastName}
                    </b>
                  </p>
                  <p className="mb-1">
                    Gender : <b>{GET_GENDER[doctor?.gender]}</b>
                  </p>
                  <p className="mb-1">
                    AreaOfExpertise : <b>{doctor?.areaOfExpertise}</b>
                  </p>
                  <p className="mb-1">
                    Degree : <b>{doctor?.degree}</b>
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="mb-1">
                    Experience : <b>{doctor?.experience}</b>
                  </p>
                  <p className="mb-1">
                    isChildRelated : <b>{doctor?.isChildRelated ? 'Yes' : 'No'}</b>
                  </p>
                  <p className="mb-1">
                    isClaimed : <b>{doctor?.isClaimed ? 'Yes' : 'No'}</b>
                  </p>
                  <p className="mb-1">
                    isVerified : <b>{doctor?.isVerified ? 'Yes' : 'No'}</b>
                  </p>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            className="me-2"
            color="primary"
            onClick={handleSave}
            disabled={isEmpty(doctor) || isEmpty(appointmentDateTime)}>
            {!isEmpty(selectedItem?.doctor) ? 'Save' : 'Add'}
          </Button>
          <Button color="light" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AssignDoctorModal;
